<template>
  <create-form
    :is-create-modal-opened="isCreateModalOpened"
    :refresh-data="getNotifications"
    @toggle-modal="toggleCreateModal"
  />
  <edit-form
    v-if="isEditModalOpened"
    :is-edit-modal-opened="isEditModalOpened"
    :selected-value="selectedNotification"
    :refresh-data="getNotifications"
    @toggle-modal="isEditModalOpened = false"
  />
  <delete-modal
    v-if="isDeleteModalOpened"
    :is-deleting="isDeleteModalOpened"
    @cancel-delete="isDeleteModalOpened = false"
    @toggle-modal="isDeleteModalOpened = false"
    @confirm-delete="openConfirmationModal('delete')"
  />
  <confirmation-modal
    :delete-text="confirmationModalText"
    :is-confirmation-modal-opened="isConfirmationModalOpened"
    @confirm-action="confirmationModalAction"
    @close-confirmation-modal="isConfirmationModalOpened = false"
    @toggle-modal="isConfirmationModalOpened = false"
  />
  <main-section>
    <loader v-if="isLoading" />
    <div v-else class="h-full">
      <table-action-header
        :icon="['fas', 'add']"
        :permission-id="permissions.CreateNotificationEmails"
        :title="$t('Notification.Title')"
        button-text="Create"
        @execute-method="toggleCreateModal"
      />
      <custom-table
        :is-data-source-loading="isDataSourceLoading"
        :options="options"
        :render-summary="['email']"
        :show-per-page="16"
        :table-data="notifications"
        :table-headers="tableHeaders"
        table-height="93%"
        :isActionListNotDisplayed="
          !permissionStore.hasPermission(permissions.EditNotificationEmails) &&
          !permissionStore.hasPermission(permissions.DeleteNotificationEmails)
        "
        @selected-rows="getSelectedRows"
        @force-refresh="getNotifications"
      />
    </div>
  </main-section>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import MainSection from "@/core/Components/MainSection.vue";
import CustomTable from "@/core/Components/CustomTable.vue";
import TableActionHeader from "@/core/Components/TableActionHeader.vue";
import CreateForm from "../Components/CreateForm.vue";
import { useToast } from "vue-toastification";
import { ErrorMessageFormatter } from "@/core/Services/GlobalFunctions";
import Loader from "@/core/Components/Loader.vue";
import EditForm from "../Components/EditForm.vue";
import DeleteModal from "@/core/Components/Notifications/DeleteModal.vue";
import ConfirmationModal from "@/core/Components/Notifications/ConfirmationModal.vue";
import { useChangeCompanyStore } from "@/core/Store/changeCompanyStore";
import { usePermissionStore } from "@/core/Store/permissionStore";
import { Permissions } from "@/core/Services/Enums";
import { TableDropdownItem } from "@/core/Services/GlobalTypes";
import NotificationService from "../Services/NotificationsService";
import { Notifications } from "../Services/NotificationsTypes";

export default defineComponent({
  components: {
    ConfirmationModal,
    DeleteModal,
    Loader,
    CreateForm,
    TableActionHeader,
    CustomTable,
    MainSection,
    EditForm,
  },
  data() {
    return {
      notificationsService: new NotificationService(),
      isCreateModalOpened: false,
      isLoading: false,
      notifications: [] as Notifications[],
      selectedNotification: {} as Notifications,
      isDataSourceLoading: false,
      isEditModalOpened: false,
      isDeleteModalOpened: false,
      isConfirmationModalOpened: false,
      confirmationModalText: "",
      toast: useToast(),
      changeCompanyStore: useChangeCompanyStore(),
      permissionStore: usePermissionStore(),
      permissions: Permissions,
      tableHeaders: {
        id: { header: "ID", visible: false },
        companyId: { header: "Notification.CompanyId", visible: false },
        email: { header: "Notification.Email", visible: true },
        active: { header: "Notification.Active", visible: true },
        dateCreate: { header: "Notification.DateCreate", visible: true },
      },
      options: {
        edit: {
          header: "Action.Edit",
          icon: ["fas", "pen"],
          permissionId: Permissions.EditNotificationEmails,
          action: this.toggleEditModal,
        },
        delete: {
          header: "Action.Delete",
          icon: ["fas", "trash"],
          permissionId: Permissions.DeleteNotificationEmails,
          action: this.toggleDeleteModal,
        },
      } as TableDropdownItem,
    };
  },
  async created() {
    this.isLoading = true;
    try {
      await this.getNotifications();
    } catch (error) {
      this.toast.error(ErrorMessageFormatter(error));
    } finally {
      this.isLoading = false;
    }
  },

  watch: {
    async "changeCompanyStore.companyId"() {
      await this.getNotifications();
    },
  },
  methods: {
    async getNotifications() {
      this.isDataSourceLoading = true;
      try {
        this.notifications = await this.notificationsService.getNotifications();
      } catch (error) {
        this.toast.error(ErrorMessageFormatter(error));
      } finally {
        this.isDataSourceLoading = false;
      }
    },
    async deleteNotification() {
      try {
        await this.notificationsService.deleteNotification(
          this.selectedNotification.id
        );
        this.toast.success((this as any).$t("Notification.DeleteSuccess"));
        this.isDeleteModalOpened = false;
        await this.getNotifications();
      } catch (error) {
        this.toast.error(ErrorMessageFormatter(error));
      }
    },
    openConfirmationModal(key: string) {
      this.isConfirmationModalOpened = true;
      this.confirmationModalText = key;
    },
    async confirmationModalAction() {
      switch (this.confirmationModalText) {
        case "delete":
          await this.deleteNotification();
          break;
      }
      this.isConfirmationModalOpened = false;
    },
    getSelectedRows(values: Notifications[]) {
      if (values.length) {
        this.selectedNotification = values[0];
      }
    },
    toggleCreateModal() {
      this.isCreateModalOpened = !this.isCreateModalOpened;
    },
    toggleEditModal() {
      this.isEditModalOpened = !this.isEditModalOpened;
    },
    toggleDeleteModal() {
      this.isDeleteModalOpened = !this.isDeleteModalOpened;
    },
  },
});
</script>

<style scoped></style>
